import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Check, Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

export const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    bereich: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { submitContactForm } = await import("@/lib/contactForm");
    const success = await submitContactForm(formData);
    if (success) setSubmitted(true);
  };

  return (
    <section className="relative section-padding bg-background" ref={ref} id="kontakt">
      {/* Background texture */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.03]"
        style={{ backgroundImage: `url(/images/cta-bg.jpg)` }}
      />

      <div className="container-wide relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          {/* Left: CTA content + contact info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 bg-card border border-border border-r-0 p-8 md:p-12 flex flex-col"
          >
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
              Jetzt starten
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground mb-4 leading-tight">
              Projekt
              <span className="text-muted-foreground block">anfragen.</span>
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed mb-8">
              Sie haben ein Bauvorhaben, benötigen eine Garten- und Landschaftsplanung oder suchen einen Sachverständigen? 
              Kontaktieren Sie uns – wir beraten Sie gerne.
            </p>

            {/* Benefits */}
            <div className="space-y-3 mb-10">
              {[
                "Kostenlose Erstberatung",
                "Antwort innerhalb von 24 Stunden",
                "Unverbindliches Angebot",
                "Regionaler Service",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 gradient-blue flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-foreground" />
                  </div>
                  <span className="text-sm text-foreground font-body">{item}</span>
                </div>
              ))}
            </div>

            {/* Contact details */}
            <div className="mt-auto space-y-4 pt-8 border-t border-border">
              <h3 className="font-heading font-semibold text-sm text-foreground uppercase tracking-wider mb-4">
                Kontaktinformationen
              </h3>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">Telefon</p>
                  <p className="text-sm text-foreground font-body font-medium">+49 (0) 531 87 46 10</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">E-Mail</p>
                  <p className="text-sm text-foreground font-body font-medium">info@freitag-bs.de</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">Adresse</p>
                  <p className="text-sm text-foreground font-body font-medium">Musterstraße 42, 12345 Musterstadt</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-body">Öffnungszeiten</p>
                  <p className="text-sm text-foreground font-body font-medium">Mo – Fr: 10:00 – 14:00 Uhr</p>
                </div>
              </div>
            </div>

            {/* Color accent */}
            <div className="flex gap-0.5 mt-8">
              <div className="w-12 h-1 gradient-blue" />
              <div className="w-12 h-1 gradient-orange" />
              <div className="w-12 h-1 gradient-green" />
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3"
          >
            {submitted ? (
              <div className="bg-card border border-border p-12 flex flex-col items-center justify-center h-full min-h-[500px]">
                <div className="w-16 h-16 gradient-blue flex items-center justify-center mb-6">
                  <Check className="w-8 h-8 text-foreground" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-foreground mb-3 text-center">
                  Vielen Dank für Ihre Anfrage!
                </h3>
                <p className="text-sm text-muted-foreground font-body text-center max-w-md mb-2">
                  Ihre Nachricht wurde erfolgreich übermittelt. Unser Team wird sich 
                  innerhalb von 24 Stunden bei Ihnen melden.
                </p>
                <p className="text-xs text-muted-foreground font-body text-center mb-6">
                  Bei dringenden Anfragen erreichen Sie uns unter +49 (0) 531 87 46 10
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: "", email: "", phone: "", bereich: "", message: "" });
                  }}
                >
                  Neue Anfrage senden
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-card border border-border p-8 lg:p-12 space-y-6 h-full">
                <div>
                  <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                    Nachricht senden
                  </h3>
                  <p className="text-xs text-muted-foreground font-body">
                    Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                      Vor- und Nachname *
                    </label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-background border-border text-foreground font-body h-11"
                      placeholder="Max Mustermann"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                      E-Mail Adresse *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-background border-border text-foreground font-body h-11"
                      placeholder="max@beispiel.de"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                      Telefonnummer
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-background border-border text-foreground font-body h-11"
                      placeholder="+49 531 87 46 10"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                      Fachbereich
                    </label>
                    <select
                      value={formData.bereich}
                      onChange={(e) => setFormData({ ...formData, bereich: e.target.value })}
                      className="flex h-11 w-full bg-background border border-border px-3 py-2 text-sm font-body text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="bau">Bauunternehmung</option>
                      <option value="galabau">Galabau</option>
                      <option value="sv">Sachverständiger</option>
                      <option value="allgemein">Allgemeine Anfrage</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-heading uppercase tracking-widest text-muted-foreground">
                    Ihre Nachricht *
                  </label>
                  <Textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-background border-border text-foreground font-body resize-none"
                    placeholder="Beschreiben Sie Ihr Projekt oder Anliegen..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Button variant="blue" size="lg" type="submit">
                    Nachricht senden
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <p className="text-[10px] text-muted-foreground font-body">
                    Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
